// Media lifecycle helpers — WL-072 / EW-010.
//
// When a content block is deleted, the editor fires `deleteWheelMedia` for the
// media it holds (BlockIndicator onConfirmDelete). The historical path had two
// defects:
//   1. NO refcount — if the same media `src` is used by more than one block on
//      the wheel (e.g. the user cloned an image block), deleting ONE copy
//      called deleteWheelMedia for that url, over-deleting media still in use
//      by the surviving block(s) → broken media on the live wheel + storage
//      accounting drift.
//   2. failures were swallowed to console only (no user-facing feedback).
//
// These pure helpers compute *which* urls are safe to release for a given
// delete by refcounting against the rest of the current wheel's blocks. This
// is a WITHIN-WHEEL refcount — it cannot see media shared across other wheels
// (that needs a server-side refcount, tracked in the register), but it fully
// closes the far-more-common same-wheel over-delete.
//
// Blocks are passed in a normalized shape `{ name, props }` so the helpers are
// framework-agnostic and unit-testable without craft.js.

// Map a craft block name -> the url(s) it references. Mirrors the exact fields
// that BlockIndicator's delete path targets, so the refcount and the delete
// stay in lockstep.
export function extractBlockMediaUrls(block) {
  if (!block || typeof block !== "object") return [];
  const { name, props } = block;
  if (!props) return [];
  const urls = [];

  switch (name) {
    case "ImageWidget":
    case "ImageWithText":
      if (props.image?.src) urls.push(props.image.src);
      break;
    case "VideoWidget":
      if (props.src) urls.push(props.src);
      break;
    case "ImageGridWidget":
    case "CarouselWidget":
      // Both hold a `images: [{ src }]` list; release every image url the block
      // owns (DL-041 carousel shares ImageGrid's image shape).
      (props.images || []).forEach((img) => {
        if (img?.src) urls.push(img.src);
      });
      break;
    case "DocumentWidget":
      (props.documents || []).forEach((doc) => {
        if (doc?.src) urls.push(doc.src);
      });
      break;
    case "AudioWidget":
      (props.audios || []).forEach((aud) => {
        if (aud?.src) urls.push(aud.src);
      });
      break;
    default:
      break;
  }

  return urls.filter(Boolean);
}

/**
 * Given the block being deleted and every OTHER block still on the wheel,
 * return the de-duplicated list of media urls that are safe to release —
 * i.e. referenced by the deleted block but by no surviving block.
 *
 * @param {{name:string, props:object}} deletedBlock
 * @param {Array<{name:string, props:object}>} survivingBlocks
 * @returns {string[]}
 */
export function collectDeletableMediaUrls(deletedBlock, survivingBlocks = []) {
  const candidates = extractBlockMediaUrls(deletedBlock);
  if (candidates.length === 0) return [];

  const stillReferenced = new Set();
  for (const block of survivingBlocks) {
    for (const url of extractBlockMediaUrls(block)) {
      stillReferenced.add(url);
    }
  }

  const seen = new Set();
  const deletable = [];
  for (const url of candidates) {
    if (!stillReferenced.has(url) && !seen.has(url)) {
      seen.add(url);
      deletable.push(url);
    }
  }
  return deletable;
}
